import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { Building2, Users, Home, KeyRound, Shield, AlertTriangle, Activity, Pause, Play, Crown, Plus, Settings as SettingsIcon, Copy } from 'lucide-react';
import { toast } from 'sonner';
import OrgManageDrawer from '@/components/OrgManageDrawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

// ─────────────────────────────────────────────────────────────────────────────
// Platform Owner dashboard
// Only visible when user.custom_role === 'platform_owner'.
// Lives at /platform.
// ─────────────────────────────────────────────────────────────────────────────
export default function Platform() {
  const { permissions } = useAuth();
  const isPlatformOwner = permissions?.role === 'platform_owner';

  const [tab, setTab] = useState('orgs');
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manageOrg, setManageOrg] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    if (!isPlatformOwner) return;
    setLoading(true);
    try {
      const [s, o, p, t, a] = await Promise.all([
        api.platformStats(), api.platformOrgs(), api.platformPlans(),
        api.platformTemplates(), api.platformAuditLog({ limit: 100 }),
      ]);
      setStats(s.data); setOrgs(o.data); setPlans(p.data); setTemplates(t.data); setAudit(a.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load platform data');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!isPlatformOwner) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto mt-20 p-8 rounded-2xl border border-amber-200 bg-amber-50 text-center">
          <Shield className="h-10 w-10 mx-auto text-amber-600" />
          <h1 className="mt-4 text-xl font-semibold text-amber-900">Platform Owner access required</h1>
          <p className="mt-2 text-sm text-amber-800">This area is restricted to the global platform administrator.</p>
        </div>
      </Layout>
    );
  }

  const suspend = async (orgId) => {
    const reason = window.prompt('Reason for suspension (optional)?');
    try { await api.platformSuspendOrg(orgId, reason || null); toast.success('Org suspended'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const activate = async (orgId) => {
    try { await api.platformActivateOrg(orgId); toast.success('Org activated'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <Layout>
      <div className="max-w-[1240px] mx-auto px-6 py-8" data-testid="platform-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#0E1320] text-white text-[10.5px] font-bold tracking-[0.18em] reva-mono uppercase">
              <Crown className="h-3 w-3" /> Platform Owner
            </div>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-[#0E1320]">Global control plane</h1>
            <p className="mt-1 text-[14px] text-[#4A5263]">Every organization, subscription, plan, and audit event across REVA.</p>
          </div>
          <CreateOrgDialog open={createOpen} setOpen={setCreateOpen} plans={plans} onCreated={load} />
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-8">
          {[
            { k: 'orgs_total',           label: 'Organizations',  icon: Building2 },
            { k: 'orgs_sandbox',         label: 'Sandbox orgs',   icon: Activity },
            { k: 'orgs_suspended',       label: 'Suspended',      icon: AlertTriangle },
            { k: 'users_total',          label: 'Users',          icon: Users },
            { k: 'properties_total',     label: 'Properties',     icon: Home },
            { k: 'units_total',          label: 'Units',          icon: KeyRound },
            { k: 'tenants_total',        label: 'Tenants',        icon: Users },
            { k: 'subscriptions_active', label: 'Active subs',    icon: Shield },
            { k: 'deletion_requests_pending', label: 'Pending deletes', icon: AlertTriangle },
          ].map(({ k, label, icon: Icon }) => (
            <div key={k} className="rounded-xl border border-[#E6EAF0] bg-white p-4" data-testid={`platform-stat-${k}`}>
              <div className="flex items-center justify-between">
                <Icon className="h-4 w-4 text-[#00734A]" strokeWidth={1.7} />
                <span className="text-[10px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">{label}</span>
              </div>
              <div className="mt-3 text-2xl font-semibold text-[#0E1320]">{loading ? '—' : (stats?.[k] ?? 0)}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#E6EAF0] mb-6" role="tablist">
          {['orgs', 'plans', 'templates', 'audit'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              data-testid={`platform-tab-${t}`}
              className={`px-4 py-2.5 text-[13px] font-medium transition-colors border-b-2 ${
                tab === t ? 'border-[#00734A] text-[#0E1320]' : 'border-transparent text-[#4A5263] hover:text-[#0E1320]'
              }`}>
              {t === 'orgs' ? 'Organizations' : t === 'plans' ? 'Subscription plans' : t === 'templates' ? 'Permission templates' : 'Audit log'}
            </button>
          ))}
        </div>

        {/* Orgs tab */}
        {tab === 'orgs' && (
          <div className="rounded-xl border border-[#E6EAF0] bg-white overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="bg-[#FBFCFD] text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">
                <tr>
                  <th className="text-start px-4 py-3 font-medium">Organization</th>
                  <th className="text-start px-4 py-3 font-medium">Type</th>
                  <th className="text-start px-4 py-3 font-medium">Users</th>
                  <th className="text-start px-4 py-3 font-medium">Properties</th>
                  <th className="text-start px-4 py-3 font-medium">Subscription</th>
                  <th className="text-start px-4 py-3 font-medium">Status</th>
                  <th className="text-end px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orgs.map(o => (
                  <tr key={o.id} className="border-t border-[#F1F4F7]" data-testid={`platform-org-row-${o.id}`}>
                    <td className="px-4 py-3 text-[#0E1320] font-medium">{o.name || o.id}</td>
                    <td className="px-4 py-3">
                      {o.is_sandbox ? (
                        <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-medium">Sandbox</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium">Production</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#4A5263]">{o.stats?.users ?? 0}</td>
                    <td className="px-4 py-3 text-[#4A5263]">{o.stats?.properties ?? 0}</td>
                    <td className="px-4 py-3 text-[#4A5263]">
                      {o.subscription ? <span className="reva-mono text-[12px]">{o.subscription.plan_id} · {o.subscription.seat_limit} seats</span> : <span className="text-[#9CA3AF]">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {o.is_suspended ? (
                        <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-[11px] font-medium">Suspended</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium">Active</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-end">
                      <div className="inline-flex items-center gap-1.5">
                        <button onClick={() => setManageOrg(o)} data-testid={`platform-manage-${o.id}`}
                          className="inline-flex items-center gap-1 text-[12px] font-medium text-[#0E1320] hover:text-[#00734A] border border-[#E6EAF0] hover:border-[#00734A] rounded-md px-2 py-1">
                          <SettingsIcon className="h-3 w-3" /> Manage
                        </button>
                        {o.is_suspended ? (
                          <button onClick={() => activate(o.id)} data-testid={`platform-activate-${o.id}`}
                            className="inline-flex items-center gap-1 text-[12px] font-medium text-emerald-700 hover:text-emerald-900">
                            <Play className="h-3.5 w-3.5" /> Activate
                          </button>
                        ) : (
                          <button onClick={() => suspend(o.id)} data-testid={`platform-suspend-${o.id}`}
                            className="inline-flex items-center gap-1 text-[12px] font-medium text-red-700 hover:text-red-900">
                            <Pause className="h-3.5 w-3.5" /> Suspend
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {orgs.length === 0 && !loading && (
                  <tr><td colSpan={7} className="text-center px-4 py-10 text-[#71777E]">No organizations yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Plans tab */}
        {tab === 'plans' && (
          <div className="grid md:grid-cols-3 gap-4">
            {plans.map(p => (
              <div key={p.id} className="rounded-xl border border-[#E6EAF0] bg-white p-5" data-testid={`platform-plan-${p.id}`}>
                <div className="text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#71777E]">{p.is_system ? 'System plan' : 'Custom plan'}</div>
                <h3 className="mt-2 text-lg font-semibold text-[#0E1320]">{p.name_en}</h3>
                <p className="mt-1 text-[12px] text-[#4A5263]">{p.headline_en}</p>
                <dl className="mt-4 space-y-1.5 text-[12.5px] text-[#4A5263]">
                  <div className="flex justify-between"><dt>Seats</dt><dd className="text-[#0E1320] font-medium">{p.seat_limit}</dd></div>
                  <div className="flex justify-between"><dt>Properties</dt><dd className="text-[#0E1320] font-medium">{p.properties_limit}</dd></div>
                  <div className="flex justify-between"><dt>Modules</dt><dd className="text-[#0E1320] font-medium">{p.modules?.length ?? 0}</dd></div>
                  <div className="flex justify-between"><dt>Price · SAR/mo</dt><dd className="text-[#0E1320] font-medium">{p.price_sar_monthly || '—'}</dd></div>
                </dl>
              </div>
            ))}
          </div>
        )}

        {/* Templates tab */}
        {tab === 'templates' && (
          <div className="rounded-xl border border-[#E6EAF0] bg-white overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="bg-[#FBFCFD] text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">
                <tr>
                  <th className="text-start px-4 py-3 font-medium">Template</th>
                  <th className="text-start px-4 py-3 font-medium">Modules granted</th>
                  <th className="text-start px-4 py-3 font-medium">Delete portfolio</th>
                  <th className="text-start px-4 py-3 font-medium">Type</th>
                </tr>
              </thead>
              <tbody>
                {templates.map(t => (
                  <tr key={t.id} className="border-t border-[#F1F4F7]" data-testid={`platform-template-${t.id}`}>
                    <td className="px-4 py-3 text-[#0E1320] font-medium">{t.name_en} <span className="text-[#9CA3AF] text-[11px] ms-1">{t.name_ar}</span></td>
                    <td className="px-4 py-3 text-[#4A5263] reva-mono text-[11.5px]">{t.allowed_modules?.length || 0} modules</td>
                    <td className="px-4 py-3">
                      {t.can_delete_portfolio
                        ? <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-[11px] font-medium">Allowed</span>
                        : <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium">Blocked</span>}
                    </td>
                    <td className="px-4 py-3 text-[#71777E] text-[12px]">{t.is_system ? 'System' : 'Custom'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Audit tab */}
        {tab === 'audit' && (
          <div className="rounded-xl border border-[#E6EAF0] bg-white">
            <ul className="divide-y divide-[#F1F4F7]" data-testid="platform-audit-list">
              {audit.map(e => (
                <li key={e.id} className="px-4 py-3 flex items-start gap-3 text-[12.5px]">
                  <span className="px-2 py-0.5 rounded-full bg-[#F1F4F7] text-[#0E1320] text-[10.5px] font-medium reva-mono uppercase tracking-[0.12em]">{e.action}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#0E1320]"><span className="font-medium">{e.entity_type}</span> · {e.entity_id || '—'}</div>
                    <div className="text-[#71777E]">{e.actor_email || e.actor_id} · {e.actor_role || '?'} · {e.organization_id || '_platform_'}</div>
                  </div>
                  <span className="text-[11px] text-[#71777E] reva-mono whitespace-nowrap">{e.created_at?.slice(0, 19).replace('T', ' ')}</span>
                </li>
              ))}
              {audit.length === 0 && <li className="text-center px-4 py-10 text-[#71777E]">No audit events yet.</li>}
            </ul>
          </div>
        )}
      </div>

      {manageOrg && <OrgManageDrawer org={manageOrg} onClose={() => setManageOrg(null)} onMutate={load} />}
    </Layout>
  );
}

// ─── Create Org Dialog ───────────────────────────────────────────────────
function CreateOrgDialog({ open, setOpen, plans, onCreated }) {
  const [form, setForm] = useState({
    organization_name: '', organization_name_ar: '',
    owner_email: '', owner_full_name: '', owner_phone: '',
    plan_id: 'plan_plus', seat_limit: '', properties_limit: '', language: 'en',
  });
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = { ...form };
      if (!body.seat_limit) delete body.seat_limit; else body.seat_limit = Number(body.seat_limit);
      if (!body.properties_limit) delete body.properties_limit; else body.properties_limit = Number(body.properties_limit);
      const r = await api.createOrgWithOwner(body);
      setCreated(r.data);
      onCreated?.();
      toast.success('Organization created');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Creation failed');
    } finally { setSubmitting(false); }
  };

  const reset = () => {
    setForm({ organization_name: '', organization_name_ar: '', owner_email: '', owner_full_name: '', owner_phone: '', plan_id: 'plan_plus', seat_limit: '', properties_limit: '', language: 'en' });
    setCreated(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="platform-create-org">
          <Plus className="me-1.5 h-4 w-4" /> Create organization
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New organization + Property Owner</DialogTitle>
        </DialogHeader>
        {created ? (
          <div className="space-y-3" data-testid="platform-create-org-success">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3.5">
              <div className="text-emerald-900 font-semibold text-[13px]">Organization created</div>
              <dl className="mt-2 text-[12.5px] text-emerald-900 space-y-0.5">
                <div className="flex justify-between"><dt>Org name:</dt><dd className="reva-mono">{created.organization?.name}</dd></div>
                <div className="flex justify-between"><dt>Owner email:</dt><dd className="reva-mono">{created.owner_user?.email}</dd></div>
              </dl>
              {created.access_link?.url && (
                <div className="mt-3">
                  <Label className="text-[11px] text-emerald-900">Invite link (share with the new Property Owner — expires in 7 days)</Label>
                  <div className="mt-1 flex items-center gap-1.5">
                    <code className="flex-1 text-[10.5px] bg-white border border-emerald-200 px-2 py-1 rounded reva-mono break-all">{window.location.origin}{created.access_link.url}</code>
                    <button onClick={() => { navigator.clipboard.writeText(window.location.origin + created.access_link.url); toast.success('Copied'); }} className="p-1.5 hover:bg-emerald-100 rounded" data-testid="platform-copy-invite">
                      <Copy size={13} />
                    </button>
                  </div>
                </div>
              )}
            </div>
            <Button variant="outline" onClick={() => { setOpen(false); reset(); }} className="w-full" data-testid="platform-create-org-done">Done</Button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Organization name (EN) *</Label><Input required value={form.organization_name} onChange={e => setForm({ ...form, organization_name: e.target.value })} data-testid="org-name-en" /></div>
              <div><Label>Organization name (AR)</Label><Input value={form.organization_name_ar} onChange={e => setForm({ ...form, organization_name_ar: e.target.value })} dir="rtl" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Property Owner email *</Label><Input type="email" required value={form.owner_email} onChange={e => setForm({ ...form, owner_email: e.target.value })} data-testid="org-owner-email" /></div>
              <div><Label>Property Owner full name *</Label><Input required value={form.owner_full_name} onChange={e => setForm({ ...form, owner_full_name: e.target.value })} data-testid="org-owner-name" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone (optional)</Label><Input value={form.owner_phone} onChange={e => setForm({ ...form, owner_phone: e.target.value })} /></div>
              <div><Label>Language</Label>
                <select value={form.language} onChange={e => setForm({ ...form, language: e.target.value })} className="w-full h-9 px-2 border border-[#E6EAF0] rounded-md text-[13px]">
                  <option value="en">English</option><option value="ar">Arabic</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Subscription plan *</Label>
              <select required value={form.plan_id} onChange={e => setForm({ ...form, plan_id: e.target.value })} className="w-full h-9 px-2 border border-[#E6EAF0] rounded-md text-[13px]" data-testid="org-plan-select">
                {plans.map(p => <option key={p.id} value={p.id}>{p.name_en} · SAR {p.price_sar_monthly}/mo · {p.seat_limit} seats</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Seat limit (override)</Label><Input type="number" placeholder="Use plan default" value={form.seat_limit} onChange={e => setForm({ ...form, seat_limit: e.target.value })} /></div>
              <div><Label>Properties limit (override)</Label><Input type="number" placeholder="Use plan default" value={form.properties_limit} onChange={e => setForm({ ...form, properties_limit: e.target.value })} /></div>
            </div>
            <Button type="submit" disabled={submitting} className="w-full" data-testid="org-submit">
              {submitting ? 'Creating…' : 'Create organization + invite owner'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
